import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { IMediaItem, TMediaItemType } from 'types/media'
import { parseFloatAndFix } from 'utils'
import { sortMediaItemsByStartTime } from 'utils/media'
import { getVisualMediaTracks } from 'containers/Timeline/MainContainer/Tracks/utils'

interface IMagnetUpdate {
  id: string
  type: TMediaItemType
  left: number
}

const computeMagnetUpdatesForTrack = (track: IMediaItem[]): IMagnetUpdate[] => {
  const sorted = sortMediaItemsByStartTime(track)

  return sorted.reduce(
    (
      {
        updates,
        currentPosition
      }: { updates: IMagnetUpdate[]; currentPosition: number },
      item: IMediaItem
    ) => {
      if (item.startTime !== currentPosition) {
        updates.push({
          id: item.id,
          type: item.type,
          left: currentPosition
        })
      }
      return {
        updates,
        currentPosition: parseFloatAndFix(currentPosition + item.duration, 3)
      }
    },
    { updates: [], currentPosition: 0 }
  ).updates
}

export const useMagnet = () => {
  const mediaItems = useSelector(projectDataSelectors.mediaItems)

  const updates = useMemo(() => {
    const tracks = getVisualMediaTracks(mediaItems)
    return tracks.flatMap(computeMagnetUpdatesForTrack)
  }, [mediaItems])

  const applyMagnet = useCallback(() => {
    dispatch(projectDataThunks.updateTimelineItems(updates))
  }, [updates])

  return { hasGaps: updates.length > 0, applyMagnet }
}
